import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { Modal } from '../components/Modal';
import { toast } from '../stores/toasts';
import { formatShort, timeAgo, formatUzs } from '../lib/format';

export function MyCarsPage() {
  const qc = useQueryClient();
  const [openAdd, setOpenAdd] = useState(false);

  const { data: cars, isLoading } = useQuery({
    queryKey: ['my-cars'],
    queryFn: () => api.myCars(),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteMyCar(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-cars'] });
      toast.success("O'chirildi");
    },
    onError: (e: any) => toast.error(e?.message || 'Xatolik'),
  });

  return (
    <>
      <div className="row-between" style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>🚗 Mening mashinalarim</h3>
        <button className="btn btn-primary btn-sm" onClick={() => setOpenAdd(true)}>
          ➕ Qo'shish
        </button>
      </div>

      {isLoading ? (
        <div className="spinner" />
      ) : cars && cars.length > 0 ? (
        cars.map((c: any) => (
          <div key={c.id} className="card">
            <div className="row-between" style={{ marginBottom: 4 }}>
              <div style={{ fontWeight: 700, fontSize: 18 }}>{c.plate}</div>
              <span className="tag primary">{c.year ?? '—'}</span>
            </div>
            <div className="muted" style={{ fontSize: 13, marginBottom: 6 }}>
              {c.brand} {c.model}{c.color ? ` · ${c.color}` : ''}
            </div>
            {c.vin && (
              <div className="muted" style={{ fontSize: 11 }}>VIN: {c.vin}</div>
            )}
            {c.orders && c.orders.length > 0 && (
              <>
                <div className="divider" />
                <div className="field-label" style={{ marginBottom: 6 }}>So'nggi ta'mirlar</div>
                {c.orders.slice(0, 3).map((o: any) => (
                  <div key={o.id} className="row-between" style={{ fontSize: 12, padding: '3px 0' }}>
                    <div>
                      {o.workshop.name} · <span className="muted">{timeAgo(o.receivedAt)}</span>
                    </div>
                    <div className="amount">{formatUzs(o.totalCached)}</div>
                  </div>
                ))}
              </>
            )}
            <div className="row" style={{ gap: 6, marginTop: 10 }}>
              <Link
                to={`/vehicle-passport?plate=${encodeURIComponent(c.plate)}`}
                className="btn btn-sm"
                style={{ flex: 1, textAlign: 'center' }}
              >
                📜 Tarix
              </Link>
              <button
                className="btn btn-sm btn-danger"
                onClick={() => {
                  if (confirm(`${c.plate} o'chirilsinmi?`)) remove.mutate(c.id);
                }}
              >
                🗑
              </button>
            </div>
          </div>
        ))
      ) : (
        <div className="empty">
          <div className="emoji">🚗</div>
          <div>Mashinalar yo'q</div>
          <button className="btn btn-primary" style={{ marginTop: 10 }} onClick={() => setOpenAdd(true)}>
            ➕ Birinchi mashinani qo'shish
          </button>
        </div>
      )}

      {openAdd && <AddCarModal onClose={() => setOpenAdd(false)} />}
    </>
  );
}

interface AddCarModalProps {
  onClose: () => void;
}

function AddCarModal({ onClose }: AddCarModalProps) {
  const qc = useQueryClient();
  const [plate, setPlate] = useState('');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [color, setColor] = useState('');

  const add = useMutation({
    mutationFn: () =>
      api.addMyCar({
        plate: plate.trim().toUpperCase(),
        brand: brand.trim(),
        model: model.trim(),
        year: year ? Number(year) : undefined,
        color: color.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-cars'] });
      toast.success("Mashina qo'shildi");
      onClose();
    },
    onError: (e: any) => toast.error(e?.message || 'Xatolik'),
  });

  function submit() {
    if (!plate.trim() || !brand.trim() || !model.trim()) {
      toast.warning('Davlat raqami, marka va modelni kiriting');
      return;
    }
    add.mutate();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="🚗 Yangi mashina"
      footer={
        <>
          <button className="btn" onClick={onClose}>Bekor</button>
          <button className="btn btn-primary" onClick={submit} disabled={add.isPending}>
            {add.isPending ? 'Saqlanmoqda...' : "Qo'shish"}
          </button>
        </>
      }
    >
      <div className="field">
        <label className="field-label">Davlat raqami *</label>
        <input
          placeholder="01A123BC"
          value={plate}
          onChange={(e) => setPlate(e.target.value.toUpperCase())}
          autoFocus
        />
      </div>
      <div className="row" style={{ gap: 8 }}>
        <div className="field" style={{ flex: 1 }}>
          <label className="field-label">Marka *</label>
          <input
            placeholder="Chevrolet"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
          />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label className="field-label">Model *</label>
          <input
            placeholder="Cobalt"
            value={model}
            onChange={(e) => setModel(e.target.value)}
          />
        </div>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <div className="field" style={{ flex: 1 }}>
          <label className="field-label">Yili</label>
          <input
            type="number"
            min="1950"
            max="2100"
            placeholder="2020"
            value={year}
            onChange={(e) => setYear(e.target.value)}
          />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label className="field-label">Rang</label>
          <input
            placeholder="Oq"
            value={color}
            onChange={(e) => setColor(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
